import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useFetch } from '../useFetch'
import { useAuth } from '../../contexts/AuthContext'

import { CONFIG } from '../../config'
import { LightSwitchState } from '../../types/court/court'
import { ApiError } from '../../types/exception/errorStatus'
import { getErrorMessage } from '../../utils/exception/exceptionUtils'

export function useLightSwitchState(lightSwitchName: string) {
  const { t } = useTranslation('exceptions')
  const [state, setState] = useState<LightSwitchState | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const { authInfo } = useAuth()
  const { fetcher } = useFetch(
    authInfo?.accessToken,
    authInfo?.refreshToken
  )

  const getState = async () => {
    if (!lightSwitchName) {
      setState(null)
      return
    }

    setLoading(true)
    try {
      const data = await fetcher<LightSwitchState>(
        `${CONFIG.BOUNCE_API}/api/v1/light-switches/${encodeURIComponent(lightSwitchName)}/state`
      )

      setState(data)
      setError(null)
      return data ?? undefined
    } catch (caughtError) {
      setError(
        caughtError instanceof ApiError
          ? getErrorMessage(caughtError.data)
          : t('exceptions:unknown')
      )
      setState(null)
      return undefined
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    // TODO: Enable this request when the backend relay state endpoint is implemented.
    // void getState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightSwitchName])

  const resetError = () => setError(null)

  return { state, loading, error, getState, resetError }
}
